import { ReactNode, useEffect, useRef, useState } from "react";
import { FixedSizeList, ListChildComponentProps, ListOnScrollProps } from "react-window";

import { Box, Button, Typography } from "@mui/material";
import ArrowCircleDownIcon from "@mui/icons-material/ArrowCircleDown";

export interface EventUserScrollColumn {
  field: string;
  headerName: string;
  // 고정 폭(px). 없으면 남은 폭을 나눠 쓴다
  width?: number;
  renderCell?: (row: any) => ReactNode;
}

interface propsType {
  rows: any[];
  columns: EventUserScrollColumn[];
  emptyText: string;
  // 행이 중간에 끼어들 수 있는 목록(응모자)은 고유 키를 준다. 없으면 순번을 키로 쓴다
  getRowId?: (row: any) => string;
  // 순번. 없으면 목록 안 위치(1부터)를 쓴다 (걸러 보는 목록에서 원래 순번을 유지할 때 준다)
  getRowNo?: (row: any) => number | undefined;
  // 배경을 칠해 눈에 띄게 할 행 (예: 이전 이벤트 당첨자)
  isRowHighlighted?: (row: any) => boolean;
  // 위쪽을 보고 있을 때 새 행이 붙으면 띄우는 이동 버튼 문구
  newRowsLabel?: string;
  onRowClick?: (row: any) => void;
}

const rowHeight: number = 42;
const visibleRowCount: number = 10;
const listHeight: number = rowHeight * visibleRowCount;
const noColumnWidth: number = 70;
const flexColumnMinWidth: number = 160;

// 응모시간 오름차순, 같으면 sk 순 (응모자·당첨자 목록 공통 정렬 기준)
export const compareApplyDtm = (a: any, b: any) => {
  const aKey = `${a.apply_dtm ?? ""}#${a.sk ?? ""}`;
  const bKey = `${b.apply_dtm ?? ""}#${b.sk ?? ""}`;
  return aKey < bKey ? -1 : aKey > bKey ? 1 : 0;
};

const cellSx = (column?: EventUserScrollColumn) => ({
  flex: column?.width ? `0 0 ${column.width}px` : "1 1 0",
  minWidth: column?.width ? column.width : flexColumnMinWidth,
  px: 1,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

// 이벤트 응모자/당첨자 목록. 10명 높이로 스크롤하고 앞에 순번을 붙인다 (보이는 행만 그린다)
const EventUserScrollList = (props: propsType) => {
  const listRef = useRef<FixedSizeList>(null);
  const scrollOffsetRef = useRef(0);
  const prevRowCountRef = useRef(0);
  const [showNewRowsButton, setShowNewRowsButton] = useState(false);

  const minWidth = noColumnWidth + props.columns.reduce((sum, column) => sum + (column.width ?? flexColumnMinWidth), 0);

  const scrollToBottom = () => {
    if (props.rows.length > 0) listRef.current?.scrollToItem(props.rows.length - 1, "end");
    setShowNewRowsButton(false);
  };

  // 새 행이 붙었을 때 맨 아래를 보고 있었으면 따라 내려가고, 위쪽을 보고 있었으면 위치를 유지하고 이동 버튼을 띄운다.
  // 처음 채워질 때는 1번부터 보여준다
  useEffect(() => {
    const prevRowCount = prevRowCountRef.current;
    prevRowCountRef.current = props.rows.length;
    if (prevRowCount === 0 || props.rows.length <= prevRowCount) return;

    const wasAtBottom = scrollOffsetRef.current + listHeight >= prevRowCount * rowHeight - rowHeight / 2;
    if (wasAtBottom) {
      listRef.current?.scrollToItem(props.rows.length - 1, "end");
    } else if (props.newRowsLabel) {
      setShowNewRowsButton(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.rows.length]);

  const onScroll = ({ scrollOffset }: ListOnScrollProps) => {
    scrollOffsetRef.current = scrollOffset;
    if (scrollOffset + listHeight >= props.rows.length * rowHeight - rowHeight / 2) setShowNewRowsButton(false);
  };

  const RenderRow = ({ index, style }: ListChildComponentProps) => {
    const row = props.rows[index];
    const highlighted = props.isRowHighlighted?.(row) ?? false;
    return (
      <Box
        style={style}
        sx={{
          display: "flex",
          alignItems: "center",
          borderBottom: "1px solid rgba(224, 224, 224, 1)",
          cursor: props.onRowClick ? "pointer" : "default",
          backgroundColor: highlighted ? "#fff4e5" : undefined,
          "&:hover": { backgroundColor: highlighted ? "#ffe8cc" : "rgba(0, 0, 0, 0.04)" },
        }}
        onClick={() => props.onRowClick?.(row)}
      >
        <Box sx={cellSx({ field: "no", headerName: "No.", width: noColumnWidth })}>
          {props.getRowNo?.(row) ?? index + 1}
        </Box>
        {props.columns.map((column) => (
          <Box key={column.field} sx={cellSx(column)}>
            {column.renderCell ? column.renderCell(row) : row[column.field]}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box sx={{ position: "relative", width: "100%", border: "1px solid rgba(224, 224, 224, 1)", borderRadius: 1 }}>
      <Box sx={{ overflowX: "auto" }}>
        <Box sx={{ minWidth: minWidth, fontSize: "0.875rem" }}>
          {/* 목록의 세로 스크롤바 폭만큼 헤더에도 자리를 남겨 열 위치를 맞춘다 */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: rowHeight,
              fontWeight: 500,
              borderBottom: "1px solid rgba(224, 224, 224, 1)",
              overflowY: "hidden",
              scrollbarGutter: "stable",
            }}
          >
            <Box sx={cellSx({ field: "no", headerName: "No.", width: noColumnWidth })}>No.</Box>
            {props.columns.map((column) => (
              <Box key={column.field} sx={cellSx(column)}>
                {column.headerName}
              </Box>
            ))}
          </Box>
          {props.rows.length === 0 ? (
            <Box sx={{ height: listHeight, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Typography variant="body2" className="color-gray">
                {props.emptyText}
              </Typography>
            </Box>
          ) : (
            <FixedSizeList
              ref={listRef}
              height={listHeight}
              width="100%"
              itemCount={props.rows.length}
              itemSize={rowHeight}
              itemKey={(index) => (props.getRowId ? props.getRowId(props.rows[index]) : index)}
              overscanCount={5}
              onScroll={onScroll}
              style={{ scrollbarGutter: "stable" }}
            >
              {RenderRow}
            </FixedSizeList>
          )}
        </Box>
      </Box>
      {showNewRowsButton && (
        <Button
          sx={{
            position: "absolute",
            bottom: 8,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1,
          }}
          variant="contained"
          size="small"
          onClick={scrollToBottom}
          endIcon={<ArrowCircleDownIcon />}
        >
          {props.newRowsLabel}
        </Button>
      )}
    </Box>
  );
};

export default EventUserScrollList;
